import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  TextField,
  Button,
  Typography,
  Container,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { debounce } from 'lodash';
import { fetchServerTime } from '../utils/dateTime';
import FormCard from './FormCard';

function ProprietarioForm({ onProprietarioAdded, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const [empresa_id, setEmpresaId] = useState('');
  const [imovel_id, setImovelId] = useState('');
  const [pessoa_id, setPessoaId] = useState('');
  const [data_inicio, setDataInicio] = useState('');
  const [data_fim, setDataFim] = useState('');
  const [empresas, setEmpresas] = useState([]);

  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [selectedImovelObject, setSelectedImovelObject] = useState(null);

  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const [selectedPessoaObject, setSelectedPessoaObject] = useState(null);

  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
        .then((response) => setImovelOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar imóveis:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );

  const debouncedFetchPessoas = useMemo(() => debounce((searchTerm) => {
      setLoadingPessoas(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setPessoaOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar pessoas:', error))
        .finally(() => setLoadingPessoas(false));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchImoveis.cancel();
      debouncedFetchPessoas.cancel();
    };
  }, [debouncedFetchImoveis, debouncedFetchPessoas]);

  useEffect(() => {
    if (user && user.tipo_usuario === 'admin') {
      api.get('/empresas')
        .then((response) => setEmpresas(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar empresas:', error));
    }

    if (initialData) {
      setEmpresaId(initialData.empresa_id);
      setImovelId(initialData.imovel_id);
      setPessoaId(initialData.pessoa_id);
      setDataInicio(initialData.data_inicio ? initialData.data_inicio.slice(0, 10) : '');
      setDataFim(initialData.data_fim ? initialData.data_fim.slice(0, 10) : '');
    } else {
      clearForm();
      fetchServerTime(api)
        .then((data) => setDataInicio(data.empresaDate || ''))
        .catch((error) => console.error('Erro ao buscar data atual do servidor:', error));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, user]);

  const clearForm = () => {
    setEmpresaId('');
    setImovelId('');
    setPessoaId('');
    setDataInicio('');
    setDataFim('');
    setSelectedImovelObject(null);
    setSelectedPessoaObject(null);
    setImovelOptions([]);
    setPessoaOptions([]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const payload = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id,
      imovel_id,
      pessoa_id,
      data_inicio,
      data_fim: data_fim || null,
    };

    if (initialData) {
      api.put(`/proprietarios/${initialData.id}`, payload)
        .then(() => {
          clearForm();
          onProprietarioAdded();
          onCancelEdit();
        })
        .catch((error) => console.error('Erro ao atualizar proprietario:', error));
    } else {
      api.post('/proprietarios', payload)
        .then(() => {
          clearForm();
          onProprietarioAdded();
        })
        .catch((error) => console.error('Erro ao adicionar proprietario:', error));
    }
  };

  return (
    <Container maxWidth="sm">
      <FormCard component="form" onSubmit={handleSubmit}>
        <Typography variant="h5" gutterBottom>
          {initialData ? 'Editar Proprietário' : 'Adicionar Novo Proprietário'}
        </Typography>

        {(user && user.tipo_usuario === 'admin') && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="empresa-select-label">Empresa</InputLabel>
            <Select
              labelId="empresa-select-label"
              value={empresa_id}
              label="Empresa"
              onChange={(e) => setEmpresaId(e.target.value)}
              disabled={!!initialData}
            >
              {empresas.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>{emp.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Autocomplete
          options={imovelOptions}
          getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedImovelObject}
          onChange={(event, newValue) => {
            setSelectedImovelObject(newValue);
            setImovelId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, value, reason) => {
            if (reason === 'input') {
              if (value) debouncedFetchImoveis(value);
              else setImovelOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Imóvel"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={!!initialData}
        />

        <Autocomplete
          options={pessoaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPessoaObject}
          onChange={(event, newValue) => {
            setSelectedPessoaObject(newValue);
            setPessoaId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, value, reason) => {
            if (reason === 'input') {
              if (value) debouncedFetchPessoas(value);
              else setPessoaOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pessoa"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={!!initialData}
        />

        <TextField
          label="Data de Início"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={data_inicio}
          onChange={(e) => setDataInicio(e.target.value)}
          required
        />

        <TextField
          label="Data de Fim"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={data_fim}
          onChange={(e) => setDataFim(e.target.value)}
        />

        <Button type="submit" variant="contained" sx={{ mt: 2, mr: 2 }}>
          {initialData ? 'Atualizar Proprietário' : 'Adicionar Proprietário'}
        </Button>
        <Button type="button" variant="outlined" sx={{ mt: 2 }} onClick={onCancelEdit}>
          Cancelar
        </Button>
      </FormCard>
    </Container>
  );
}

export default ProprietarioForm;

